import React, { useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const ContactUs = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        "service_f7fmssx",
        "template_pr3ey4h",
        {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          to_name: "ASites Planner Team",
        },
        "-rXsp2xIJ98qOBflT"
      );

      toast.success("Message sent successfully!", {
        position: "top-right",
        theme: "dark",
      });

      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message.", {
        position: "top-right",
        theme: "dark",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Container>
        <BackgroundElements>
          <FloatingCircle size="80px" top="10%" left="5%" delay="0s" />
          <FloatingCircle size="120px" top="70%" left="85%" delay="1s" />
          <FloatingCircle size="60px" top="30%" left="90%" delay="2s" />
          <GradientOrb />
        </BackgroundElements>

        <ContentWrapper>
          <LeftSection>
            <ModernHeading>
              <ModernLine>Have</ModernLine>
              <ModernLine>
                Questions<span className="accent">?</span>
              </ModernLine>
              <ModernLine accent="true">We've Got</ModernLine>
              <ModernLine>
                Answers<span className="accent">.</span>
              </ModernLine>
            </ModernHeading>

            <Description>
              Shoot us a message and we'll get back to you within 24 hours.
              Follow us on social media for updates.
            </Description>

            <InfoCards>
              <InfoCard>
                <CardIcon>✉️</CardIcon>
                <div>
                  <CardLabel>Email</CardLabel>
                  <CardValue>hello@example.com</CardValue>
                </div>
              </InfoCard>
              <InfoCard>
                <CardIcon>⏱️</CardIcon>
                <div>
                  <CardLabel>Response Time</CardLabel>
                  <CardValue>Within 24 hours</CardValue>
                </div>
              </InfoCard>
            </InfoCards>
          </LeftSection>

          <RightSection>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <FormHeader>
                <FormTitle>Send us a message</FormTitle>
                <FormSubtitle>We're here to help</FormSubtitle>
              </FormHeader>

              <FormGroup>
                <InputWrapper>
                  <StyledInput
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("name")}
                    onBlur={() => setFocusedField(null)}
                    placeholder=" "
                    required
                  />
                  <FloatingLabel
                    $focused={focusedField === "name" || formData.name}
                  >
                    Your Name
                  </FloatingLabel>
                  <InputBorder />
                </InputWrapper>

                <InputWrapper>
                  <StyledInput
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField(null)}
                    placeholder=" "
                    required
                  />
                  <FloatingLabel
                    $focused={focusedField === "email" || formData.email}
                  >
                    Your Email
                  </FloatingLabel>
                  <InputBorder />
                </InputWrapper>

                <InputWrapper>
                  <StyledTextarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    placeholder=" "
                    rows="4"
                    required
                  />
                  <FloatingLabel
                    $focused={focusedField === "message" || formData.message}
                  >
                    Your Message
                  </FloatingLabel>
                  <InputBorder />
                </InputWrapper>
              </FormGroup>

              {/* FIX: Wrapped text in spans to prevent removeChild error */}
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner />
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Message →</span>
                )}
              </SubmitButton>

              <PrivacyNote>
                By submitting, you agree to our privacy policy.
              </PrivacyNote>
            </Form>
          </RightSection>
        </ContentWrapper>
      </Container>
      <ToastContainer style={{ zIndex: 9999999999999999999999 }} />
    </>
  );
};

// --- STYLED COMPONENTS ---
// (Keeping your exact styles, just fixing the prop warning for $focused)

const Container = styled.div`
  position: relative;
  max-width: 1800px;
  padding: 6em 5%;
  border-radius: 32px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  background: rgba(255, 255, 255, 0.027);
  margin: auto;
  justify-content: center;
  /* align-items: center; */
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
`;

const FloatingCircle = styled.div`
  position: absolute;
  width: ${(props) => props.size};
  height: ${(props) => props.size};
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  border-radius: 50%;
  background: linear-gradient(45deg, #9281dd, rgba(146, 129, 221, 0.1));
  opacity: 0.15;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${(props) => props.delay};
  filter: blur(20px);
`;

const GradientOrb = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(146, 129, 221, 0.1) 0%,
    rgba(146, 129, 221, 0.05) 40%,
    transparent 70%
  );
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const ModernHeading = styled.div`
  font-family: "Montserrat", sans-serif;
`;

const ModernLine = styled.div`
  font-size: 4.5vw;
  font-weight: 400;
  line-height: 1.1;
  position: relative;
  display: block;
  color: ${(props) => (props.accent ? "transparent" : "white")};
  background: ${(props) =>
    props.accent ? "linear-gradient(45deg, #9281dd, #5e43d3)" : "none"};
  -webkit-background-clip: ${(props) => (props.accent ? "text" : "none")};
  background-clip: ${(props) => (props.accent ? "text" : "none")};

  .accent {
    color: #9281dd;
  }

  @media (max-width: 768px) {
    font-size: 12vw;
  }
`;

const Description = styled.p`
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.7);
  max-width: 400px;
`;

const InfoCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(146, 129, 221, 0.3);
    transform: translateY(-2px);
  }
`;

const CardIcon = styled.div`
  font-size: 24px;
`;
const CardLabel = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`;
const CardValue = styled.div`
  font-size: 16px;
  color: white;
`;

const RightSection = styled.div`
  width: 100%;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 28px;
  padding: 50px;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #9281dd, transparent);
  }
  @media (max-width: 450px) {
    padding: 50px 20px;
  }
`;

const FormHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media (max-width: 450px) {
    text-align: center;
  }
`;
const FormTitle = styled.h2`
  font-size: 32px;
  color: white;
  margin: 0;
`;
const FormSubtitle = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;
const InputWrapper = styled.div`
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 20px 0 10px 0;
  font-size: 16px;
  color: white;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  outline: none;
`;

const StyledTextarea = styled(StyledInput).attrs({ as: "textarea" })`
  resize: vertical;
  min-height: 100px;
`;

const FloatingLabel = styled.span`
  position: absolute;
  top: 20px;
  left: 0;
  font-size: 16px;
  pointer-events: none;
  transition: all 0.3s ease;
  transform-origin: left top;
  transform: ${(props) =>
    props.$focused ? "translateY(-20px) scale(0.8)" : "none"};
  color: ${(props) =>
    props.$focused ? "#9281dd" : "rgba(255, 255, 255, 0.5)"};
`;

const InputBorder = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #9281dd, transparent);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
  ${StyledInput}:focus ~ & {
    transform: scaleX(1);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px 40px;
  font-size: 16px;
  color: white;
  background: linear-gradient(45deg, #9281dd, #5e43d3);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(146, 129, 221, 0.3);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PrivacyNote = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  margin: 0;
`;

export default ContactUs;
